import { useState } from 'react';
import { useDeliveryTemplates } from '@/hooks/useDeliveryTemplates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Edit, Trash2, Copy, MapPin, CheckCircle2, AlertCircle } from 'lucide-react';
import { AddressDialog } from './AddressDialog';
import { DeleteAddressDialog } from './DeleteAddressDialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';

type LocationType = 'office' | 'warehouse' | 'job_site' | 'distribution_center' | 'retail_location' | 'other';

export const AddressesLibrary = () => {
  const [search, setSearch] = useState('');
  const [locationTypeFilter, setLocationTypeFilter] = useState<LocationType | 'all'>('all');
  const [sortBy, setSortBy] = useState<'location_name' | 'created_at' | 'usage_count'>('created_at');
  const [editingAddress, setEditingAddress] = useState<any>(null);
  const [deletingAddress, setDeletingAddress] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'view' | 'edit' | 'create'>('create');

  const { templates, loading, cloneTemplate, refetch } = useDeliveryTemplates({
    locationType: locationTypeFilter === 'all' ? undefined : locationTypeFilter,
    search: search || undefined,
  });

  const locationTypes: LocationType[] = [
    'office',
    'warehouse',
    'job_site',
    'distribution_center',
    'retail_location',
    'other'
  ];

  const getLocationTypeLabel = (type: LocationType) => {
    const labels: Record<LocationType, string> = {
      office: 'Office',
      warehouse: 'Warehouse',
      job_site: 'Job Site',
      distribution_center: 'Distribution Center',
      retail_location: 'Retail Location',
      other: 'Other'
    };
    return labels[type];
  };

  const sortedAddresses = [...templates].sort((a, b) => {
    if (sortBy === 'location_name') {
      return a.location_name.localeCompare(b.location_name);
    } else if (sortBy === 'created_at') {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    } else {
      return (b.usage_count || 0) - (a.usage_count || 0);
    }
  });

  const formatAddress = (address: any) => {
    const parts = [
      address.address_line1,
      address.city,
      address.state_province,
      address.postal_code
    ].filter(Boolean);
    return parts.join(', ');
  };

  const handleClone = async (address: any) => {
    await cloneTemplate(address.id, `${address.location_name} (Copy)`);
  };

  const handleRowClick = (address: any) => {
    setEditingAddress(address);
    setViewMode('view');
    setIsDialogOpen(true);
  };

  const handleEdit = (address: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingAddress(address);
    setViewMode('edit');
    setIsDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingAddress(null);
    setViewMode('create');
    setIsDialogOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      refetch();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Delivery Addresses</h1>
          <p className="text-muted-foreground mt-1">Create and manage reusable delivery locations for your jobs</p>
        </div>
        <Button onClick={handleCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          Create New Address
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, city, or state..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={locationTypeFilter} onValueChange={(v) => setLocationTypeFilter(v as any)}>
              <SelectTrigger>
                <SelectValue placeholder="All Location Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Location Types</SelectItem>
                {locationTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {getLocationTypeLabel(type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
              <SelectTrigger>
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">Date Created</SelectItem>
                <SelectItem value="location_name">Name</SelectItem>
                <SelectItem value="usage_count">Usage Count</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card>
          <CardContent className="py-12">
            <div className="flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          </CardContent>
        </Card>
      ) : sortedAddresses.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center">
                  <MapPin className="h-8 w-8 text-muted-foreground" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">No addresses found</h3>
                <p className="text-muted-foreground mt-1">
                  {search || locationTypeFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Create your first delivery address to get started'}
                </p>
              </div>
              {!search && locationTypeFilter === 'all' && (
                <Button onClick={handleCreate} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Create Your First Address
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Location Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedAddresses.map((address) => (
                  <TableRow
                    key={address.id}
                    className="cursor-pointer hover:bg-secondary/50"
                    onClick={() => handleRowClick(address)}
                  >
                    <TableCell className="font-medium">{address.location_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {getLocationTypeLabel(address.location_type as LocationType)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatAddress(address)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {address.contact_name || '-'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(address.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      {address.address_validated ? (
                        <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-muted text-muted-foreground">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Unverified
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleEdit(address, e)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClone(address);
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingAddress(address);
                          }}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <AddressDialog
        open={isDialogOpen}
        onOpenChange={handleDialogClose}
        address={editingAddress}
        mode={viewMode}
        onModeChange={setViewMode}
      />

      <DeleteAddressDialog
        open={!!deletingAddress}
        onOpenChange={(open) => !open && setDeletingAddress(null)}
        address={deletingAddress}
      />
    </div>
  );
};
